import { useState, useRef, useMemo, useLayoutEffect } from 'react';
import './docsView.scss';
import clsx from 'clsx';
import { Badge } from 'primereact/badge';
import { useMeetingStore } from '@/stores/meeting.ts';
import { useBoardPdfStore } from '@/stores/boardPdf';
import { useStudyModeStore } from '@/stores/studyMode';
import { useUserStore } from '@/stores/user.ts';
import { ParticipantRole } from '@/consts/meeting';
import Api from '@/api/api';
import { DocProps } from '@/types/api';
import CleanupWorker from '@/components/CleanupWorker';
import { useMeetingFileUpload } from '@/hooks/useMeetingFileUpload';
import { showToast } from '@/components/ToastManager';
import { useSend } from '@/components/SendProvider';

interface ActiveDocProps {
  id: string | undefined;
  name: string | undefined;
}

interface UploadDocProps extends DocProps {
  deleteDoc: (id: string) => void;
  isActive: boolean | null;
  onSetActiveDoc: (id: string | null, name: string | null) => void;
  addStudentDoc: (id: string, name: string) => void;
  downloadFile: (file: DocProps) => void;
}

interface StudentDocs extends DocProps {
  isStudent: boolean;
  removeDoc: (id: string, path: string) => void;
  downloadFile: (file: DocProps) => void;
  onSetActiveDoc: (id: string | null, name: string | null) => void;
  isActive?: boolean | null;
}

function ActiveDocumentItem({ name }: ActiveDocProps) {
  return (
    <div className="doc-item doc-item-active">
      <span className="doc-name">{name}</span>
    </div>
  );
}

function UploadDocumentItem({
  path,
  id,
  deleteDoc,
  isActive,
  onSetActiveDoc,
  addStudentDoc,
  downloadFile
}: UploadDocProps) {
  const isPracticeMode = useStudyModeStore((state) => state.isPracticeMode);
  const { role } = useUserStore();
  return (
    <div
      className={clsx(
        'doc-item',
        isActive && 'doc-item_active',
        role === ParticipantRole.TEACHER
          ? 'doc-item-upload-teacher'
          : !isPracticeMode
            ? 'doc-item-upload-assistant'
            : 'doc-item-upload-assistant-practice'
      )}
    >
      <span className="doc-name">{path.split('/').pop()}</span>
      {role === ParticipantRole.TEACHER ? (
        <div className="doc-actions">
          <button className={clsx('pi pi-trash')} onClick={() => deleteDoc(id)}></button>
          <button
            className={clsx('pi pi-user-plus')}
            onClick={() => addStudentDoc(id, path)}
          ></button>
          <button
            className={clsx('pi pi-eye', isActive && 'upload_active')}
            onClick={() => (!isActive ? onSetActiveDoc(id, path) : onSetActiveDoc(null, null))}
          ></button>
        </div>
      ) : (
        <div className="doc-actions">
          <button
            className={clsx('pi pi-download')}
            onClick={() => downloadFile({ path, id })}
          ></button>
          {isPracticeMode && role == ParticipantRole.ASSISTANT && (
            <button
              className={clsx('pi pi-eye', isActive && 'upload_active')}
              onClick={() => (!isActive ? onSetActiveDoc(id, path) : onSetActiveDoc(null, null))}
            ></button>
          )}
        </div>
      )}
    </div>
  );
}

function StudentDocumentItem({
  isStudent,
  id,
  path,
  removeDoc,
  downloadFile,
  isActive,
  onSetActiveDoc
}: StudentDocs) {
  const isPracticeMode = useStudyModeStore((state) => state.isPracticeMode);
  const { role } = useUserStore();
  return (
    <div
      className={clsx(
        'doc-item',
        !isPracticeMode || role !== ParticipantRole.STUDENT
          ? 'doc-item-student'
          : 'doc-item-student-practice',
        isActive && 'doc-item_active'
      )}
    >
      <span className="doc-name">{path.split('/').pop()}</span>
      {!isStudent ? (
        <div className="doc-actions">
          <button className="pi pi-times" onClick={() => removeDoc(id, path)}></button>
        </div>
      ) : (
        <div className="doc-actions">
          <button
            className={clsx('pi pi-download')}
            onClick={() => downloadFile({ id, path })}
          ></button>
          {isPracticeMode && role == ParticipantRole.STUDENT && (
            <button
              className={clsx('pi pi-eye', isActive && 'upload_active')}
              onClick={() => (!isActive ? onSetActiveDoc(id, path) : onSetActiveDoc(null, null))}
            ></button>
          )}
        </div>
      )}
    </div>
  );
}

function DocsView() {
  const [isOpenUploadDocs, setIsOpenUploadDocs] = useState(true);
  const [isOpenStudentDocs, setIsOpenStudentDocs] = useState(true);

  const { uploadDocs, setUploadDocs, deleteUploadDoc } = useBoardPdfStore();
  const { studentDocs, setStudentDocs, addStudentDoc, removeStudentDoc } = useBoardPdfStore();
  const meeting = useMeetingStore((state) => state.meeting);
  const meetingUid = meeting?.uuid;

  const { activeDoc, setActiveDoc } = useBoardPdfStore();
  const [cleanupQueue, setCleanupQueue] = useState<string[]>([]);

  const role = useUserStore((state) => state.role);

  const { handleFile } = useMeetingFileUpload(meetingUid);

  const inputRef = useRef<HTMLInputElement>(null);

  const { sendFileToStudents, sendUploadFiles } = useSend();

  const handleDownloadFile = (file: DocProps) => {
    const fileName = file.path.split('/').pop() || `file-${file.id}`;
    const link = document.createElement('a');
    const path = file.path.startsWith('/') ? file.path.slice(1) : file.path;
    link.href = `${location.origin}/${path}`;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  useLayoutEffect(() => {
    if (!meetingUid) return;
    const getFiles = async () => {
      if (role !== ParticipantRole.STUDENT) {
        const files = await Api.getUploadFiles(meetingUid);
        setUploadDocs(files);
      }
      const studentFiles = await Api.getStudentFiles(meetingUid);
      setStudentDocs(studentFiles);
    };
    getFiles();
  }, [meetingUid]);

  const handleDeleteRequest = (id: string) => {
    setCleanupQueue((prev) => [...prev, id]);
  };

  const handleDeleteDoc = async (id: string) => {
    if (!meetingUid) return;
    try {
      const message = { action: 'file-remove', file: { id } };
      await sendUploadFiles(new TextEncoder().encode(JSON.stringify(message)), {
        reliable: true
      });
      await Api.deleteFileFromMeeting(meetingUid, id);
      handleDeleteRequest(id);
      deleteUploadDoc(id);
      removeStudentDoc(id);
      if (activeDoc?.id === id) {
        setActiveDoc({ path: '', id: '' });
      }
    } catch (err) {
      console.error(err);
      showToast(
        'errorToast',
        'error',
        'Ошибка удаления файла',
        'Не удалось удалить файл. Попробуйте позже'
      );
      return;
    }
  };

  const handleMakeActiveDoc = (id: string | null = null, path: string | null = null) => {
    if (!meetingUid) return;

    if (id && path) {
      if (role === ParticipantRole.TEACHER) {
        Api.setActiveFile(meetingUid, id);
      }
      setActiveDoc({ id, path });
    } else {
      setActiveDoc({ id: '', path: '' });
    }
  };

  const handleAddStudentDoc = async (id: string, path: string) => {
    if (!meetingUid) return;
    try {
      await Api.shareFileWithStudentInMeeting(meetingUid, id);
      addStudentDoc({ id, path });
      const message = { action: 'student-file-add', file: { id, path } };
      await sendFileToStudents(new TextEncoder().encode(JSON.stringify(message)), {
        reliable: true
      });
    } catch (err) {
      console.error(err);
      showToast(
        'errorToast',
        'error',
        'Ошибка отправки файла',
        'Не удалось поделиться файлом с учениками. Попробуйте позже'
      );
      return;
    }
  };

  const handleRemoveStudentDoc = async (id: string, path: string) => {
    if (!meetingUid) return;
    try {
      await Api.unshareFileWithStudentInMeeting(meetingUid, id);
      removeStudentDoc(id);
      const message = {
        action: 'student-file-remove',
        file: { id, path }
      };
      await sendFileToStudents(new TextEncoder().encode(JSON.stringify(message)), {
        reliable: true
      });
    } catch (err) {
      console.error(err);
      showToast(
        'errorToast',
        'error',
        'Ошибка удаления файла у учеников',
        'Не удалось удалить файл у учеников. Попробуйте позже'
      );
      return;
    }
  };

  const sortedUploadDocs = useMemo(() => {
    return [...uploadDocs].sort((a, b) =>
      a.path.localeCompare(b.path, undefined, { sensitivity: 'base' })
    );
  }, [uploadDocs]);

  const sortedStudentDocs = useMemo(() => {
    return [...studentDocs].sort((a, b) =>
      a.path.localeCompare(b.path, undefined, { sensitivity: 'base' })
    );
  }, [studentDocs]);

  return (
    <div className="docs-view">
      {role === ParticipantRole.TEACHER && (
        <div className="active-doc">
          <h1>Активный документ</h1>
          <div className="active-doc-field">
            {activeDoc && activeDoc.path !== '' ? (
              <ActiveDocumentItem id={activeDoc?.id} name={activeDoc?.path.split('/').pop()} />
            ) : (
              <div className="no-active-doc">Нет активного документа</div>
            )}
          </div>
        </div>
      )}
      {(role === ParticipantRole.TEACHER || role === ParticipantRole.ASSISTANT) && (
        <div className="upload-docs">
          <div
            className="docs-view__section-header"
            onClick={() => uploadDocs.length !== 0 && setIsOpenUploadDocs(!isOpenUploadDocs)}
          >
            <h1
              className={clsx(
                'docs-view__section-header_title',
                uploadDocs.length === 0 && 'empty-section'
              )}
            >
              Загруженные документы
              <Badge
                value={uploadDocs.length}
                className="docs-view__section-header_docs-count"
              ></Badge>
              {uploadDocs.length !== 0 && (
                <span
                  className={clsx(
                    'docs-view__section-header_arrow',
                    isOpenUploadDocs ? 'pi pi-chevron-down' : 'pi pi-chevron-up'
                  )}
                ></span>
              )}
            </h1>
          </div>
          <div className="upload-doc-field">
            <ul className={clsx('docs-list', !isOpenUploadDocs && 'closed')}>
              {sortedUploadDocs.map((doc) => (
                <li key={doc.id}>
                  <UploadDocumentItem
                    id={doc.id}
                    path={doc.path}
                    deleteDoc={handleDeleteDoc}
                    isActive={activeDoc?.path !== '' && activeDoc?.id === doc.id}
                    onSetActiveDoc={handleMakeActiveDoc}
                    addStudentDoc={handleAddStudentDoc}
                    downloadFile={handleDownloadFile}
                  />
                </li>
              ))}
            </ul>
            {role === ParticipantRole.TEACHER && (
              <div className="doc-item add-doc" onClick={() => inputRef.current?.click()}>
                <input
                  type="file"
                  accept="application/pdf"
                  hidden
                  ref={inputRef}
                  onChange={handleFile}
                />
                <span className="plus pi pi-file-plus"></span>
              </div>
            )}
          </div>
        </div>
      )}
      <div className="docs-for-students">
        <div
          className="docs-view__section-header"
          onClick={() => studentDocs.length !== 0 && setIsOpenStudentDocs(!isOpenStudentDocs)}
        >
          <h1
            className={clsx(
              'docs-view__section-header_title',
              studentDocs.length === 0 && 'empty-section'
            )}
          >
            Документы для учеников
            <Badge
              value={studentDocs.length}
              className="docs-view__section-header_docs-count"
            ></Badge>
            {studentDocs.length !== 0 && (
              <span
                className={clsx(
                  'docs-view__section-header_arrow',
                  isOpenStudentDocs ? 'pi pi-chevron-down' : 'pi pi-chevron-up'
                )}
              ></span>
            )}
          </h1>
        </div>
        <div className="docs-for-students-field">
          <ul className={clsx('docs-list', !isOpenStudentDocs && 'closed')}>
            {sortedStudentDocs.map((doc) => (
              <li key={doc.id}>
                <StudentDocumentItem
                  isStudent={role !== ParticipantRole.TEACHER}
                  id={doc.id}
                  path={doc.path}
                  removeDoc={handleRemoveStudentDoc}
                  onSetActiveDoc={handleMakeActiveDoc}
                  isActive={
                    role !== ParticipantRole.TEACHER &&
                    activeDoc?.path !== '' &&
                    activeDoc?.id === doc.id
                  }
                  downloadFile={handleDownloadFile}
                />
              </li>
            ))}
          </ul>
        </div>
      </div>
      {role === ParticipantRole.TEACHER && cleanupQueue.length > 0 && (
        <CleanupWorker
          key={cleanupQueue[0]}
          currentDeletingDocId={cleanupQueue[0]}
          onDone={(doneId: string) => setCleanupQueue((prev) => prev.filter((i) => i !== doneId))}
        />
      )}
    </div>
  );
}

export default DocsView;
